/**
 * #433: when the assistant adds a fact from chat, a floating copy of it "flies"
 * from the chat to its landing row in the general facts notebook, so the agent
 * sees WHERE their chat turn landed instead of a list silently growing
 * somewhere off-screen.
 *
 * Shared by both hosts because the two layouts differ only in where the chat
 * is: Neptune has it below the facts in one 320px column, Venus beside them in
 * its own pane. The flight is therefore anchored to an ELEMENT rather than to
 * window coordinates — first choice the add_fact tool call itself (`ToolCallList`
 * marks it with `data-fact-flight-origin`), since that is the thing on screen
 * the fact came out of; then the chat (`originRef`). The same choreography works
 * vertically and horizontally.
 *
 * The landing row is located by `data-fact-id` / `data-fact-text`, which
 * `FactRow` already renders, so a host needs no markup of its own.
 */
import * as React from "react";
import { createPortal } from "react-dom";

// Timings. The pill is ON SCREEN on the first frame -- no fade-in, no drift, no
// waiting-for-a-target hover: it appears where the tool call is and the only
// motion it ever makes is the one trip to its row. An "appearing" step read as
// the fact being computed elsewhere and then delivered, which is backwards: it
// already exists, it is only being moved.
//
/** The glide onto the row. */
const GLIDE_MS = 550;
/** The pill's fade begins here and ends exactly at GLIDE_MS, on the landing
 *  spot, with the real row already revealed underneath. */
const FADE_START_MS = 350;
const FADE_MS = GLIDE_MS - FADE_START_MS;
/** Unmount after the fade has fully finished. */
const UNMOUNT_AFTER_LAND_MS = FADE_MS + 50;
/** Row position is "settled" after this many identical measurements. Sample
 *  count and interval are a pair: the row must hold still for
 *  (STABLE_SAMPLES x SETTLE_POLL_MS) ms, and that product is what stops the
 *  pill being aimed mid-scroll and overshooting. Polling got faster to shorten
 *  the flight, so the count went up to keep the window ~the same. */
const STABLE_SAMPLES = 3;
const SETTLE_POLL_MS = 60;
const SETTLE_MAX_ATTEMPTS = 30;
/** Absolute safety: land regardless, so a pill can never hang mid-air. */
const ABORT_MS = 3000;
/** How long the host's chat auto-scroll stays suppressed: the settle window
 *  plus the flight plus a margin, not a flat "long enough for anything". */
const SUPPRESS_SCROLL_MS = STABLE_SAMPLES * SETTLE_POLL_MS + GLIDE_MS + 400;

/**
 * Is the trip long enough to be worth animating?
 *
 * A pill that launches on top of its destination travels almost nowhere, which
 * reads as a glitch rather than a hand-off, so those flights are skipped and the
 * fact simply appears.
 *
 * Measured as the DISTANCE between the two points, not as overlap between the
 * chat's box and the row's. Overlap was wrong for a chat that is an overlay:
 * Venus centres its drawer across both panes, so its box intersects nearly any
 * fact row while the actual flight is the width of a pane — the animation was
 * suppressed exactly where it was most useful.
 */
const MIN_FLIGHT_PX = 96;

function farEnoughToAnimate(
  from: { x: number; y: number },
  to: { x: number; y: number },
): boolean {
  return Math.hypot(to.x - from.x, to.y - from.y) >= MIN_FLIGHT_PX;
}

/**
 * Just enough of a fact to fly one. Deliberately structural rather than the
 * `MessageFact` UI type: hosts pass their GraphQL rows straight in,
 * and those carry `deletedAt`, which the UI type has no reason to.
 */
export interface FlyableFact {
  id: string;
  content: string;
  customerRequestId?: string | null;
  isAiGenerated?: boolean;
  deletedAt?: string | null;
}

/**
 * What a render should do about the facts it was just handed.
 *
 * Split out of the effect because the loading case is subtle and worth pinning:
 * `facts` is undefined until the query resolves, and seeding the baseline from
 * that produced an EMPTY baseline. Every fact then looked new when the data
 * arrived, so reloading a ticket that already had chat-added facts replayed the
 * animation -- which reads as the tool having run again, since a flying pill
 * means "a fact was just added".
 *
 * `undefined`/`null` means not loaded; an empty array means loaded with no
 * facts, which IS a valid baseline.
 */
export function decideFlight(
  facts: ReadonlyArray<FlyableFact> | undefined | null,
  seen: ReadonlySet<string> | null,
): { action: "wait" } | { action: "seed"; ids: string[] } | { action: "fly"; fact: FlyableFact } {
  if (facts == null) return { action: "wait" };
  // Only ticket-level AI facts (customerRequestId null) added via chat; skip prefill
  // bullets (customerRequestId set) and human facts.
  const general = facts.filter((f) => f.isAiGenerated && !f.customerRequestId && !f.deletedAt);
  if (seen === null) return { action: "seed", ids: general.map((f) => f.id) };
  const fresh = general.find((f) => !seen.has(f.id));
  return fresh ? { action: "fly", fact: fresh } : { action: "seed", ids: general.map((f) => f.id) };
}

interface FlyingFact {
  id: string;
  text: string;
  from: { x: number; y: number };
  /** Landing point (target row's text top-left). null while still unknown —
   *  the pill drifts until it is provided. */
  to: { x: number; y: number } | null;
}

/**
 * The floating pill itself. It is fully painted at the origin on its first
 * frame and holds there, motionless, only for as long as it takes the landing
 * row's position to settle (~180ms) — then one glide, text-to-text, onto the
 * row. No fade-in and no drift: the fact is not arriving from anywhere, it is
 * being moved.
 *
 * `onLanded` fires exactly as the landing fade begins. Rendered via a portal to
 * <body> (fixed-positioned: unaffected by any scroll container). Decorative.
 */
function FlyingFactOverlay({
  text,
  from,
  to,
  onLanded,
}: {
  text: string;
  from: { x: number; y: number };
  to: { x: number; y: number } | null;
  onLanded: () => void;
}) {
  const [phase, setPhase] = React.useState<"spawn" | "target">("spawn");
  const elRef = React.useRef<HTMLDivElement | null>(null);
  const textRef = React.useRef<HTMLSpanElement | null>(null);
  // The pill's UN-transformed base text position, measured at first paint.
  // Deltas are computed against this (transforms are absolute, so mid-drift
  // retargeting still needs the base anchor, not the current position).
  const baseTextPosRef = React.useRef<{ left: number; top: number } | null>(
    null,
  );
  // Exact translation, TEXT-to-TEXT: pill text node -> target row text node.
  const [delta, setDelta] = React.useState<{ dx: number; dy: number } | null>(
    null,
  );
  const landedRef = React.useRef(false);
  // Keep the latest onLanded in a ref so the landing timer stays STABLE across
  // parent re-renders (the parent re-renders constantly while the chat streams;
  // a restarting timer was what once delayed the reveal "until things went quiet").
  const onLandedRef = React.useRef(onLanded);
  React.useEffect(() => {
    onLandedRef.current = onLanded;
  }, [onLanded]);
  const land = React.useCallback(() => {
    if (landedRef.current) return;
    landedRef.current = true;
    onLandedRef.current();
  }, []);

  // Mount: measure the base anchor on the first painted frame. Measured, not
  // animated -- the pill is already fully visible by then, and the delta the
  // glide uses is relative to this un-transformed position.
  React.useEffect(() => {
    const raf = requestAnimationFrame(() => {
      const rect = (textRef.current ?? elRef.current)?.getBoundingClientRect();
      baseTextPosRef.current = rect
        ? { left: rect.left, top: rect.top }
        : { left: from.x, top: from.y };
    });
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Safety: if a target never arrives (layout never settles, or the parent's
  // timers were torn down), don't hang there — land after a generous window.
  React.useEffect(() => {
    const abort = setTimeout(land, ABORT_MS);
    return () => clearTimeout(abort);
  }, [land]);

  // Target known: start the one glide and schedule the landing.
  React.useEffect(() => {
    if (!to || delta) return;
    const base = baseTextPosRef.current ?? { left: from.x, top: from.y };
    setDelta({ dx: to.x - base.left, dy: to.y - base.top });
    setPhase("target");
    // Fire the landing EXACTLY as the pill's fade begins — the row is revealed
    // under it.
    const timer = setTimeout(land, FADE_START_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [to, delta, land]);

  const targeting = phase === "target" && delta !== null;
  return createPortal(
    <div
      ref={elRef}
      aria-hidden="true"
      onTransitionEnd={(e) => {
        if (targeting && e.propertyName === "transform") land();
      }}
      style={{
        position: "fixed",
        left: from.x,
        top: from.y,
        zIndex: 2147483647,
        maxWidth: 280,
        pointerEvents: "none",
        willChange: "transform, opacity",
        // No scale: scaling shifts the edges, and the pill must fade out at
        // exactly the added row's position for a seamless hand-off.
        transform: targeting
          ? `translate(calc(-50% + ${delta.dx}px), ${delta.dy}px)`
          : "translate(-50%, 0)",
        // Fully opaque from the first frame through the travel; fades right on
        // the landing spot while the real row sits under it.
        opacity: targeting ? 0 : 1,
        // No transition before the glide: the pill must not animate INTO
        // existence, so its only transition is the one that moves it.
        transition: targeting
          ? `transform ${GLIDE_MS}ms cubic-bezier(0.35, 0.55, 0.3, 1), opacity ${FADE_MS}ms ease-in ${FADE_START_MS}ms`
          : "none",
      }}
      className="flex items-center gap-1.5 rounded-lg border border-accent/60 bg-card px-3 py-1.5 text-sm text-ink shadow-xl ring-2 ring-accent/25"
    >
      <span ref={textRef} className="line-clamp-2">
        {text}
      </span>
    </div>,
    document.body,
  );
}

export interface UseFlyingFactOptions {
  /** Every agent-contributed fact on the open message. The hook picks the
   *  general AI ones itself (customerRequestId null, isAiGenerated, not deleted) — those
   *  are the ones a chat turn adds. */
  facts: ReadonlyArray<FlyableFact> | undefined | null;
  /** The open message. A change resets the baseline so switching tickets never
   *  flies the facts that were already there. */
  messageId: string | null | undefined;
  /** The chat element the pill launches from when no add_fact tool call is on
   *  screen. Its on-screen rect is measured per flight, so a resized/moved pane
   *  is handled without any host math. Falls back to bottom-centre of the
   *  viewport when unset or unmounted. */
  originRef?: React.RefObject<HTMLElement | null>;
  /** One-shot exact launch point (e.g. where the user just clicked "Ja" to
   *  confirm the add-fact tool call), used when the tool call itself is not in
   *  the DOM — a fact confirmed via the pending-action card, say. Read and
   *  cleared the instant a flight starts; unset or already-consumed falls back
   *  to `originRef`'s rect. */
  originPointRef?: React.RefObject<{ x: number; y: number } | null>;
  /** Neptune gates its chat auto-scroll while a pill is airborne, so the chat
   *  cannot drag the view back down mid-flight. Hosts whose chat scrolls in a
   *  separate container (Venus) do not need it. */
  onFlightChange?: (flying: boolean) => void;
}

/**
 * Returns the overlay to render (or null). Mount it once, anywhere — it
 * portals to <body>.
 */
export function useFlyingFact({
  facts,
  messageId,
  originRef,
  originPointRef,
  onFlightChange,
}: UseFlyingFactOptions): React.ReactNode {
  const [flyingFact, setFlyingFact] = React.useState<FlyingFact | null>(null);
  const seenGeneralFactIdsRef = React.useRef<Set<string> | null>(null);
  const lastFlyMessageIdRef = React.useRef<string | null>(null);
  // Held in a ref so a host that re-creates the callback each render does not
  // restart the flight effect.
  const onFlightChangeRef = React.useRef(onFlightChange);
  React.useEffect(() => {
    onFlightChangeRef.current = onFlightChange;
  }, [onFlightChange]);

  React.useEffect(() => {
    const msgId = messageId ?? null;
    // A message switch resets the baseline: otherwise opening another ticket
    // would fly every fact it already has.
    const switched = lastFlyMessageIdRef.current !== msgId;
    if (switched) {
      lastFlyMessageIdRef.current = msgId;
      seenGeneralFactIdsRef.current = null;
    }
    const decision = decideFlight(facts, seenGeneralFactIdsRef.current);
    if (decision.action === "wait") return;
    if (decision.action === "seed") {
      seenGeneralFactIdsRef.current = new Set(decision.ids);
      return;
    }
    const fresh = decision.fact;
    seenGeneralFactIdsRef.current = new Set([
      ...(seenGeneralFactIdsRef.current ?? []),
      ...(facts ?? []).map((f) => f.id),
    ]);

    let cancelled = false;
    const timers: number[] = [];
    const rowFor = (id: string) =>
      document.querySelector<HTMLElement>(`[data-fact-id="${id}"]`);

    const scrollToFact = () => {
      if (cancelled) return;
      const row = rowFor(fresh.id);
      if (!row) return;
      const rr = row.getBoundingClientRect();
      // Only scroll when the row is at/above the viewport centre; never drag
      // the view downward. scrollIntoView walks whichever scroll container the
      // row is in, so this works for Neptune's single column and for Venus's
      // separate left pane alike.
      if (rr.top < window.innerHeight * 0.5) {
        row.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    };

    const revealRow = () => {
      const el = rowFor(fresh.id);
      if (!el) return;
      el.style.transition = "";
      el.style.opacity = "1";
    };

    const originRect = () => {
      const r = originRef?.current?.getBoundingClientRect();
      return r && (r.width > 0 || r.height > 0) ? r : null;
    };

    /** The on-screen add_fact tool call the fact came out of, if the chat is
     *  showing one. `ToolCallList` stamps `data-fact-flight-origin` on the row
     *  for that call, and the LAST one is the call that just ran — that element
     *  is literally where the operator's eyes are, so it beats both the click
     *  point and the chat's own box. Works for every host without any wiring:
     *  Neptune passes no click point at all, which is why its flights all used
     *  to start at the bottom of the chat. */
    const toolCallRect = () => {
      const nodes = document.querySelectorAll<HTMLElement>(
        "[data-fact-flight-origin]",
      );
      const el = nodes.length ? nodes[nodes.length - 1] : null;
      const r = el?.getBoundingClientRect();
      return r && r.width > 0 && r.height > 0 && r.bottom > 0 && r.top < window.innerHeight
        ? r
        : null;
    };

    /** Where the pill launches: the add_fact tool call on screen, else the
     *  exact point the user just clicked (e.g. the "Ja" confirm button), else
     *  the chat element's own position, so the flight reads as "this came out
     *  of the conversation" in either layout. */
    const originPoint = () => {
      const tc = toolCallRect();
      if (tc) {
        return {
          x: Math.min(Math.max(tc.left + tc.width / 2, 8), window.innerWidth - 8),
          y: Math.min(Math.max(tc.top + tc.height / 2, 8), window.innerHeight - 8),
        };
      }
      const exact = originPointRef?.current;
      if (exact) {
        return {
          x: Math.min(Math.max(exact.x, 8), window.innerWidth - 8),
          y: Math.min(Math.max(exact.y, 8), window.innerHeight - 8),
        };
      }
      const r = originRect();
      if (!r) {
        return { x: window.innerWidth / 2, y: window.innerHeight - 80 };
      }
      // Horizontal centre of the chat, near its bottom edge where the newest
      // turn sits — clamped into the viewport so an off-screen or very tall
      // chat still spawns somewhere visible.
      return {
        x: Math.min(Math.max(r.left + r.width / 2, 8), window.innerWidth - 8),
        y: Math.min(Math.max(r.bottom - 40, 8), window.innerHeight - 8),
      };
    };

    const start = () => {
      if (cancelled) return;
      const rowEl = rowFor(fresh.id);
      if (!rowEl) return;

      // Launching on top of the destination: nothing to show, so leave the row
      // as it is (already visible). Measured against the row's CURRENT position
      // -- the scroll below may move it, and the settled check repeats this.
      const launchPoint = originPoint();
      // One-shot: consumed for this flight only, so a later fact (typed, not
      // confirmed via a click) doesn't launch from a stale click point.
      if (originPointRef) originPointRef.current = null;
      const rowRect = rowEl.getBoundingClientRect();
      if (!farEnoughToAnimate(launchPoint, { x: rowRect.left, y: rowRect.top })) {
        return;
      }

      onFlightChangeRef.current?.(true);
      timers.push(
        window.setTimeout(
          () => onFlightChangeRef.current?.(false),
          SUPPRESS_SCROLL_MS,
        ),
      );

      // 0. Hide the freshly-added row (keeps its layout space so the list does
      //    not jump and the pill has a fixed target); revealed on landing.
      rowEl.style.opacity = "0";

      // 1. Spawn the pill at the tool call, fully visible on its first frame.
      setFlyingFact({
        id: fresh.id,
        text: fresh.content,
        from: launchPoint,
        to: null,
      });

      // 2. One smooth scroll, on the next frame — late enough that the pill has
      //    painted first, early enough that it is not a delay. It now runs
      //    CONCURRENTLY with the settle polling below instead of ahead of it,
      //    which is what removed the old pre-flight hover: the pill no longer
      //    waits out a fixed delay for the regeneration's layout shift, only
      //    until the row it is aiming at stops moving.
      requestAnimationFrame(scrollToFact);

      // 3. Hand the pill its TARGET only once the row's position has SETTLED
      //    (same position on several consecutive checks). Targeting off a fixed
      //    timer measured the row mid-scroll, so the pill overshot.
      let lastTop: number | null = null;
      let stable = 0;
      let attempts = 0;
      const giveUp = () => {
        // Never settled / row gone — skip the glide, just show the fact.
        revealRow();
        setFlyingFact(null);
      };
      const launchWhenSettled = () => {
        if (cancelled) return;
        attempts += 1;
        const row = rowFor(fresh.id);
        // Aim at the row's actual TEXT span — the overlay aligns its own text
        // node to this exact point (text-to-text), no padding guesswork.
        const r = (
          row?.querySelector<HTMLElement>("[data-fact-text]") ?? row
        )?.getBoundingClientRect();
        if (!r) {
          if (attempts < SETTLE_MAX_ATTEMPTS)
            timers.push(window.setTimeout(launchWhenSettled, SETTLE_POLL_MS));
          else giveUp();
          return;
        }
        const settled = lastTop !== null && Math.abs(r.top - lastTop) < 1;
        stable = settled ? stable + 1 : 0;
        lastTop = r.top;
        if (stable < STABLE_SAMPLES) {
          if (attempts < SETTLE_MAX_ATTEMPTS)
            timers.push(window.setTimeout(launchWhenSettled, SETTLE_POLL_MS));
          else giveUp();
          return;
        }

        // The scroll may have brought the row to where the pill launched from.
        // Gliding a few pixels looks broken — bail out to a plain reveal.
        if (!farEnoughToAnimate(launchPoint, { x: r.left, y: r.top })) {
          giveUp();
          return;
        }

        // Launch the waiting pill at the settled landing point.
        setFlyingFact((prev) =>
          prev?.id === fresh.id
            ? { ...prev, to: { x: r.left, y: r.top } }
            : prev,
        );
        // Fade the real row in DURING the glide (ease-in: faint early, fully
        // there by landing) so there is never an empty white row waiting —
        // the text materialises while the pill approaches it.
        if (row) {
          row.style.transition = `opacity ${GLIDE_MS}ms ease-in`;
          row.style.opacity = "1";
        }
        // Safety net only, in case the pill/overlay is torn down early.
        timers.push(window.setTimeout(revealRow, GLIDE_MS + 250));
      };
      timers.push(window.setTimeout(launchWhenSettled, SETTLE_POLL_MS));
    };

    // Wait for the new row to render into the DOM before measuring it.
    const raf = requestAnimationFrame(() => requestAnimationFrame(start));
    return () => {
      cancelled = true;
      cancelAnimationFrame(raf);
      timers.forEach((id) => window.clearTimeout(id));
      // Never leave a hidden row behind or the host's chat scroll disabled
      // (e.g. if the message changes mid-flight).
      revealRow();
      onFlightChangeRef.current?.(false);
    };
    // originRef is a stable ref object; re-running on it would restart flights.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [facts, messageId]);

  if (!flyingFact) return null;
  return (
    <FlyingFactOverlay
      text={flyingFact.text}
      from={flyingFact.from}
      to={flyingFact.to}
      onLanded={() => {
        // Reveal the real fact row the INSTANT the pill lands — no fade-in on
        // the text, it's simply there as the pill fades out on top of it.
        const el = document.querySelector<HTMLElement>(
          `[data-fact-id="${flyingFact.id}"]`,
        );
        if (el) {
          el.style.transition = "";
          el.style.opacity = "1";
        }
        // Unmount the pill only after its own fade completes.
        window.setTimeout(() => setFlyingFact(null), UNMOUNT_AFTER_LAND_MS);
      }}
    />
  );
}
